/**
 * The browser's own copy of the plans it is working on.
 *
 * What the backend does own now lives in `mealPlansService`: listing plans,
 * and creating one. What it does not own is a plan's meals — there is no
 * endpoint to read or write those — so the builder edits the copy kept here,
 * and this module publishes to whoever is subscribed.
 *
 * It therefore holds two kinds of plan: the ones the create endpoint has just
 * made (`addPlan`), and the ones edited in this sitting. Both are what the
 * list screens show in place of, or on top of, the rows they fetched — see
 * `sessionIds`.
 *
 * It starts empty. There was a third kind until recently — a dozen invented
 * plans seeded at start-up, under client ids no workspace has — and they were
 * shown on top of whatever the API answered with, so every plan list opened on
 * rows nobody had written. The fixtures they came from are test-only now; see
 * `testing/planSeed`.
 *
 * All of it is per browser session. A reload empties it, so anything written
 * in a sitting is gone by the next one.
 */

import type { PlanRecord } from "../types/plans.types";

let plans: PlanRecord[] = [];

/**
 * Which of the above were made in this sitting.
 *
 * The two list screens read their rows from `/api/v1/meal-plans/` now, but a
 * plan created here has never been near that endpoint - there is nothing to
 * POST it with yet. So the screens show these on top of what the server
 * returned, and this is how they know which ones those are. Without it they
 * would have to show the whole store, seed plans and all, on top of the real
 * list.
 */
const sessionIds = new Set<string>();

/**
 * And which of those the server has never heard of at all.
 *
 * A subset of `sessionIds`: editing a fetched plan makes it a session plan,
 * but only `addPlan` makes one the list has no row for. The lists page through
 * the server now, so the two cannot be treated alike — a plan edited from page
 * two is not on page one, and showing every session plan on top would put it
 * there a second time.
 */
const createdIds = new Set<string>();

/**
 * And which have been deleted here.
 *
 * There is no DELETE endpoint either, so the server keeps returning a plan
 * the user has just been told was removed. The screens drop these from what
 * they fetched, which is what keeps the list agreeing with the toast. Like
 * everything else in this file it lasts as long as the tab does.
 */
const deletedIds = new Set<string>();

/** Rebuilt on every change rather than filtered per call, so these keep their
 *  identity between them - `useSyncExternalStore` compares snapshots by
 *  reference, and a fresh array each time is an infinite re-render. */
let sessionPlans: PlanRecord[] = [];
let createdPlans: PlanRecord[] = [];
let deletedPlanIds: string[] = [];

const listeners = new Set<() => void>();

const publish = () => {
  sessionPlans = plans.filter((plan) => sessionIds.has(plan.id));
  createdPlans = plans.filter((plan) => createdIds.has(plan.id));
  deletedPlanIds = Array.from(deletedIds);
  listeners.forEach((listener) => listener());
};

/**
 * Subscribes to every change to the list, and returns the unsubscribe.
 * `useSyncExternalStore` is what calls this — see `usePlans`.
 */
export const subscribeToPlans = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/**
 * The current list. The reference only changes when something below replaces
 * it, which is what lets `useSyncExternalStore` use it as a snapshot.
 */
export const getPlans = (): PlanRecord[] => plans;

/**
 * The plans this sitting has made or changed - what the list screens show in
 * place of, or on top of, the server's rows. See `sessionIds`.
 */
export const getSessionPlans = (): PlanRecord[] => sessionPlans;

/**
 * Only the ones made here - what the lists show on top of the server's first
 * page, since no page of it holds them. See `createdIds`.
 */
export const getCreatedPlans = (): PlanRecord[] => createdPlans;

/** The plans this sitting has deleted, which the server still returns. */
export const getDeletedPlanIds = (): string[] => deletedPlanIds;

export const getPlan = (planId: string): PlanRecord | undefined =>
  plans.find((plan) => plan.id === planId);

/**
 * Takes a plan the backend has just created and keeps it here too.
 *
 * The record arrives from `createMealPlan` already carrying what the server
 * decided - its `public_id`, its `plan_code`, and that it is a draft. What it
 * does not carry is anywhere to put the meals: there is no endpoint to save
 * those to, so the builder edits this copy.
 *
 * It goes in as a session plan, which is what puts it at the top of both
 * lists - the server returned its rows before this plan existed.
 */
/**
 * Puts the server's copy of a plan here so the builder can edit it.
 *
 * Not a session plan: nothing about it differs from what the backend holds
 * yet, so the lists have no reason to show this in place of the row they
 * fetched. Editing it is what makes it one — see `savePlan`.
 *
 * A plan already here is left alone. It is either being edited, in which case
 * the fetched copy is the stale one, or it is the same record twice.
 */
export const hydratePlan = (record: PlanRecord): void => {
  if (plans.some((plan) => plan.id === record.id)) return;

  plans = [...plans, record];
  publish();
};

export const addPlan = (record: PlanRecord): PlanRecord => {
  // Newest first, which is the order the table shows them in.
  plans = [record, ...plans];
  sessionIds.add(record.id);
  createdIds.add(record.id);
  publish();
  return record;
};

/**
 * Writes an edited plan back over the one with the same id.
 *
 * Editing one is what makes it a session plan, whether it started here or on
 * the server: the browser now holds a version the backend has not been told
 * about, and the lists have to show that one rather than the stale row they
 * fetched. Sending a plan and saving it as a draft both come through here.
 */
export const savePlan = (plan: PlanRecord): void => {
  plans = plans.map((current) => (current.id === plan.id ? plan : current));
  sessionIds.add(plan.id);
  publish();
};

/**
 * Empties everything this module holds.
 *
 * Test-only. The store is a module singleton, so without it a plan one test
 * creates is still on the list the next one reads. Nothing in the app calls
 * this: a reload is what empties it there.
 */
export const resetPlansStore = (): void => {
  plans = [];
  sessionIds.clear();
  createdIds.clear();
  deletedIds.clear();
  publish();
};

export const deletePlan = (planId: string): void => {
  plans = plans.filter((plan) => plan.id !== planId);
  sessionIds.delete(planId);
  createdIds.delete(planId);
  deletedIds.add(planId);
  publish();
};
